import { useState } from 'react';
import clsx from 'clsx';
import CaretIcon from '../../../../../../../../assets/Svg/CaretIcon';
import Button from '../../../../../../../../common/_custom/Button/Button';
import CollapsibleBody from '../../../../../../../../common/_custom/CollapsibleBody/CollapsibleBody';
import SourceFileIcon from '../../../../../../../../common/SourceFileTag/SourceFileIcon';
import { REFERENCE_DOCUMENT_LABEL } from '../../../../../../../../common/_custom/CtaPill/ctaPillConstants';
import { formatDateByFormat, DATE_FORMATS } from '../../../../../../../../utils/dateUtils';
import { inrAmount2dp } from '../../../../../../../../utils/formatters/inrAmount';
import OutlookPill from '../../../CreditReport/components/OutlookPill/OutlookPill';
import { BASE_AMOUNT_CURRENCY, DRAWER_TABLE_COLUMNS, EMPTY_CELL } from '../../../CreditReport/CreditReportConstants';
import type { ProbeCreditRatingDate, ProbeCreditRatingInstrument } from '../../../CreditReport/creditReport.types';
import styles from './CreditRatingDatePanel.module.scss';

interface CreditRatingDatePanelProps {
    ratingDate: ProbeCreditRatingDate;
    onOpenReferenceDocument: (docId: string, fileName: string) => void;
    drawerMountedAt: number;
}

const cellText = (value: string | null) => value?.trim() || EMPTY_CELL;

const amountText = ({ amount, currency }: ProbeCreditRatingInstrument) => {
    if (amount === null) return EMPTY_CELL;
    const formattedAmount = inrAmount2dp(amount);
    const currencyCode = currency?.trim();
    return currencyCode && currencyCode !== BASE_AMOUNT_CURRENCY ? `${formattedAmount} ${currencyCode}` : formattedAmount;
};

function CreditRatingDatePanel({ ratingDate, onOpenReferenceDocument, drawerMountedAt }: CreditRatingDatePanelProps) {
    const [expanded, setExpanded] = useState(ratingDate.isLatest);
    const referenceDocument = ratingDate.isLatest ? ratingDate.referenceDocument : null;

    return (
        // One block per date so its sticky date row and column titles are pushed out by the next date, not stacked.
        <div>
            <div className={styles.DateRow}>
                <Button
                    type="button"
                    className={styles.DateToggle}
                    aria-expanded={expanded}
                    onClick={() => setExpanded((wasExpanded) => !wasExpanded)}
                    text={
                        <span className={styles.DateToggleContent}>
                            <span className={clsx(styles.Chevron, { [styles.ChevronCollapsed]: !expanded })} aria-hidden="true">
                                <CaretIcon />
                            </span>
                            <span className={styles.Date}>
                                {formatDateByFormat(ratingDate.date, DATE_FORMATS.DATE_FORMAT_DD_MM_YY) || EMPTY_CELL}
                            </span>
                        </span>
                    }
                />
                {referenceDocument && (
                    <Button
                        type="button"
                        className={styles.ReferenceDocument}
                        onClick={() => onOpenReferenceDocument(referenceDocument.mnemosyneDocId, referenceDocument.fileName)}
                        text={
                            <span className={styles.ReferenceDocumentContent}>
                                <SourceFileIcon className={styles.ReferenceDocumentIcon} />
                                {REFERENCE_DOCUMENT_LABEL}
                            </span>
                        }
                    />
                )}
            </div>
            <CollapsibleBody isOpen={expanded} drawerMountedAt={drawerMountedAt} bodyClassName={styles.TableWrap}>
                <table className={styles.Table}>
                    <colgroup>
                        {DRAWER_TABLE_COLUMNS.map(({ label, widthClass }) => (
                            <col key={label} className={clsx(widthClass && styles[widthClass])} />
                        ))}
                    </colgroup>
                    <thead>
                        <tr>
                            {DRAWER_TABLE_COLUMNS.map(({ label, alignRight }) => (
                                <th key={label} className={clsx(alignRight && styles.Amount)}>
                                    {label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {ratingDate.instruments.flatMap((instrument, instrumentIndex) =>
                            instrument.details.map((detail, detailIndex) => {
                                const isFirstDetail = detailIndex === 0;
                                const isGroupEnd =
                                    instrument.details.length > 1 &&
                                    detailIndex === instrument.details.length - 1 &&
                                    instrumentIndex < ratingDate.instruments.length - 1;
                                return (
                                    <tr key={`${instrumentIndex}-${detailIndex}`} className={clsx({ [styles.GroupEnd]: isGroupEnd })}>
                                        <td className={styles.Instrument}>{isFirstDetail ? cellText(instrument.instrument) : ''}</td>
                                        <td className={styles.Amount}>{isFirstDetail ? amountText(instrument) : ''}</td>
                                        <td>{cellText(detail.rating)}</td>
                                        <td>{cellText(detail.action)}</td>
                                        <td className={styles.Outlook}>
                                            <OutlookPill outlook={detail.outlook} />
                                        </td>
                                        <td>{cellText(detail.remarks)}</td>
                                    </tr>
                                );
                            }),
                        )}
                    </tbody>
                </table>
            </CollapsibleBody>
        </div>
    );
}

export default CreditRatingDatePanel;
